import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  LogOut, Megaphone, Copy, Users, MousePointerClick, ShoppingBag, Wallet,
  Coins, Download, Share2, CreditCard, ArrowDownToLine, MessageSquare,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useUnreadMessages from "@/hooks/useUnreadMessages";
import { Loader, EmptyState, StatusBadge } from "@/components/common";
import { formatFCFA } from "@/lib/format";
import { COMMISSION_STATUS, WITHDRAWAL_STATUS } from "@/lib/labels";
import Modal, { TextField, SelectField, TextArea } from "@/components/Modal";
import { toast } from "sonner";

const TABS = [["referrals", "Mes filleuls"], ["commissions", "Commissions"], ["withdrawals", "Retraits"]];

export default function InfluencerDashboard() {
  const { user, logout } = useAuth();
  const msgUnread = useUnreadMessages();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [tab, setTab] = useState("referrals");
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const qrRef = useRef(null);

  const load = useCallback(async () => {
    const [me, r, c, w] = await Promise.all([
      api.get("/influencer/me"), api.get("/influencer/referrals"),
      api.get("/influencer/commissions"), api.get("/influencer/withdrawals"),
    ]);
    setData(me.data); setReferrals(r.data); setCommissions(c.data); setWithdrawals(w.data);
  }, []);
  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  if (data === null) return <div className="min-h-screen bg-[#0F172A] flex items-center justify-center"><Loader label="Chargement de votre espace…" /></div>;
  if (data === false) return <div className="min-h-screen bg-[#0F172A] flex items-center justify-center text-slate-200 p-6 text-center">Profil influenceur introuvable. Contactez l'administrateur.</div>;

  const inf = data.influencer;
  const stats = data.stats;
  const link = `${window.location.origin}/r/${inf.code}`;

  const copy = (txt, msg) => { navigator.clipboard.writeText(txt); toast.success(msg); };
  const shareWhatsApp = () => {
    const text = `Découvrez TOGOBATI — matériaux & installation au Togo. Profitez via mon lien : ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };
  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `togobati-qr-${inf.code}.png`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-[#0B1220] text-slate-100 pb-16" data-testid="influencer-dashboard">
      <header className="sticky top-0 z-40 bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#C85A32] flex items-center justify-center"><Megaphone className="w-5 h-5 text-white" /></div>
            <div><p className="font-display font-extrabold leading-none">Espace Influenceur</p><p className="text-[11px] text-slate-400">{user?.name}</p></div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate("/messages")} className="text-slate-400 p-2 relative" data-testid="influencer-messages"><MessageSquare className="w-5 h-5" />{msgUnread > 0 && <span className="absolute top-0 right-0 bg-[#DC2626] text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{msgUnread}</span>}</button>
            <button onClick={() => { logout(); navigate("/login"); }} className="text-slate-400 p-2" data-testid="influencer-logout"><LogOut className="w-5 h-5" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5 space-y-5">
        {/* Parrainage card */}
        <div className="rounded-2xl bg-gradient-to-br from-[#C85A32] to-[#8B3A1E] p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
            <div ref={qrRef} className="bg-white p-3 rounded-xl shrink-0" data-testid="influencer-qr">
              <QRCodeCanvas value={link} size={150} level="M" includeMargin={false} />
            </div>
            <div className="flex-1 w-full min-w-0">
              <p className="text-xs uppercase tracking-wider text-amber-100/80 font-semibold">Votre code de parrainage</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-display text-3xl font-black" data-testid="influencer-code">{inf.code}</span>
                <button onClick={() => copy(inf.code, "Code copié")} className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25" data-testid="copy-code"><Copy className="w-4 h-4" /></button>
              </div>
              <p className="text-xs text-amber-100/80 mt-3 font-semibold">Votre lien de suivi</p>
              <div className="flex items-center gap-2 mt-1 bg-black/20 rounded-lg px-3 py-2">
                <span className="text-sm truncate flex-1" data-testid="influencer-link">{link}</span>
                <button onClick={() => copy(link, "Lien copié")} className="p-1.5 rounded-lg bg-white/15 hover:bg-white/25 shrink-0" data-testid="copy-link"><Copy className="w-4 h-4" /></button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={shareWhatsApp} className="text-xs font-bold bg-white text-[#8B3A1E] px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="share-whatsapp"><Share2 className="w-4 h-4" /> Partager sur WhatsApp</button>
                <button onClick={downloadQR} className="text-xs font-bold bg-white/15 px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="download-qr"><Download className="w-4 h-4" /> Télécharger le QR</button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat icon={MousePointerClick} label="Clics" value={stats.clicks} />
          <Stat icon={Users} label="Filleuls" value={stats.referred} />
          <Stat icon={ShoppingBag} label="Commandes" value={stats.orders_count} />
          <Stat icon={Coins} label="Gains totaux" value={formatFCFA(stats.earned)} accent />
          <Stat icon={Wallet} label="Disponible" value={formatFCFA(stats.available)} accent />
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={() => setShowWithdraw(true)} className="bg-[#E08B00] text-[#0F172A] font-bold text-sm px-4 py-2.5 rounded-lg inline-flex items-center gap-1.5" data-testid="request-withdrawal"><ArrowDownToLine className="w-4 h-4" /> Demander un retrait</button>
          <button onClick={() => setShowPayment(true)} className="bg-white/10 text-white font-bold text-sm px-4 py-2.5 rounded-lg inline-flex items-center gap-1.5" data-testid="set-payment"><CreditCard className="w-4 h-4" /> Moyen de paiement</button>
        </div>
        <p className="text-[11px] text-slate-400 -mt-2">Commission : {inf.commission_type === "percent" ? `${inf.commission_value}% par commande` : `${formatFCFA(inf.commission_value)} par commande`} · Retrait minimum {formatFCFA(data.min_withdrawal)}.</p>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#0F172A] rounded-xl p-1 border border-slate-700 w-full sm:w-fit">
          {TABS.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`flex-1 sm:flex-none text-sm font-semibold px-4 py-2 rounded-lg transition ${tab === k ? "bg-[#C85A32] text-white" : "text-slate-400 hover:text-white"}`} data-testid={`inf-tab-${k}`}>{l}</button>
          ))}
        </div>

        <div className="bg-[#0F172A] border border-slate-700 rounded-xl overflow-hidden">
          {tab === "referrals" && (referrals.length ? (
            <Table head={["Client", "Date"]} rows={referrals.map((r) => [r.client_name || r.client_email, new Date(r.created_at).toLocaleDateString("fr-FR")])} testidPrefix="ref-row" />
          ) : <DarkEmpty title="Aucun filleul pour l'instant" subtitle="Partagez votre lien pour commencer à gagner des commissions." />)}

          {tab === "commissions" && (commissions.length ? (
            <Table head={["Commande", "Client", "Base", "Commission", "Statut"]}
              rows={commissions.map((c) => [c.order_number, c.client_name, formatFCFA(c.base_amount), formatFCFA(c.amount), <StatusBadge key={c.id} map={COMMISSION_STATUS} value={c.status} />])} testidPrefix="com-row" />
          ) : <DarkEmpty title="Aucune commission" subtitle="Vos commissions apparaîtront ici une fois les commandes de vos filleuls livrées." />)}

          {tab === "withdrawals" && (withdrawals.length ? (
            <Table head={["N°", "Montant", "Moyen", "Statut", "Référence"]}
              rows={withdrawals.map((w) => [w.withdrawal_number, formatFCFA(w.amount), w.method, <StatusBadge key={w.id} map={WITHDRAWAL_STATUS} value={w.status} />, w.reference || "—"])} testidPrefix="wd-row" />
          ) : <DarkEmpty title="Aucun retrait" subtitle="Demandez un retrait dès que votre solde atteint le minimum." />)}
        </div>
      </main>

      {showWithdraw && <WithdrawModal available={stats.available} min={data.min_withdrawal} defaultMethod={inf.payment_method} defaultDetails={data.payment_details} onClose={() => setShowWithdraw(false)} onDone={load} />}
      {showPayment && <PaymentModal method={inf.payment_method} details={data.payment_details} onClose={() => setShowPayment(false)} onDone={load} />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-[#0F172A] border border-slate-700 rounded-xl p-3">
      <Icon className={`w-5 h-5 ${accent ? "text-[#E08B00]" : "text-slate-400"}`} />
      <p className="text-lg font-display font-extrabold mt-1.5">{value}</p>
      <p className="text-[11px] text-slate-400">{label}</p>
    </div>
  );
}

function Table({ head, rows, testidPrefix }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-400 border-b border-slate-700">{head.map((h) => <th key={h} className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wide">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-800 last:border-0" data-testid={`${testidPrefix}-${i}`}>
              {r.map((c, j) => <td key={j} className="px-4 py-2.5 text-slate-200">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DarkEmpty({ title, subtitle }) {
  return (
    <div className="py-14 text-center px-6" data-testid="dark-empty">
      <p className="font-display text-lg font-bold text-slate-200">{title}</p>
      <p className="text-sm text-slate-400 mt-1">{subtitle}</p>
    </div>
  );
}

function WithdrawModal({ available, min, defaultMethod, defaultDetails, onClose, onDone }) {
  const [amount, setAmount] = useState(String(min));
  const [method, setMethod] = useState(defaultMethod || "momo");
  const [details, setDetails] = useState(defaultDetails || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/influencer/withdrawals", { amount: Number(amount), method, details });
      toast.success("Demande de retrait envoyée");
      onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Demander un retrait" onClose={onClose} testid="withdraw-modal">
      <form onSubmit={submit} className="space-y-3">
        <p className="text-sm text-slate-500">Solde disponible : <strong className="text-[#C85A32]">{formatFCFA(available)}</strong>. Minimum : {formatFCFA(min)}.</p>
        <TextField label="Montant (FCFA)" type="number" value={amount} onChange={setAmount} required testid="wd-amount" />
        <SelectField label="Moyen de paiement" value={method} onChange={setMethod} placeholder={null}
          options={[{ value: "momo", label: "Mobile Money" }, { value: "virement", label: "Virement bancaire" }, { value: "autre", label: "Autre" }]} testid="wd-method" />
        <TextArea label="Coordonnées de paiement" value={details} onChange={setDetails} placeholder="Numéro Mobile Money / RIB / autre" testid="wd-details" />
        <button disabled={busy} className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg disabled:opacity-50" data-testid="wd-submit">{busy ? "Envoi…" : "Envoyer la demande"}</button>
      </form>
    </Modal>
  );
}

function PaymentModal({ method, details, onClose, onDone }) {
  const [m, setM] = useState(method || "momo");
  const [d, setD] = useState(details || "");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.put("/influencer/payment", { method: m, details: d }); toast.success("Moyen de paiement enregistré"); onDone(); onClose(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title="Moyen de paiement" onClose={onClose} testid="payment-modal">
      <form onSubmit={submit} className="space-y-3">
        <SelectField label="Type" value={m} onChange={setM} placeholder={null}
          options={[{ value: "momo", label: "Mobile Money" }, { value: "virement", label: "Virement bancaire" }, { value: "autre", label: "Autre" }]} testid="pay-method" />
        <TextArea label="Coordonnées" value={d} onChange={setD} placeholder="Numéro Mobile Money / RIB / autre" testid="pay-details" />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="pay-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}
